import { Component, OnInit, Input, Output, EventEmitter, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MessageService } from 'primeng/api';
import { ActivityReservationService } from '../activity-reservation.service';
import { ActivityService } from '@app/_components/activity';
import { AlertService } from '@app/_services';
import { ReservationService } from '@app/_components/reservation';
import { first } from 'rxjs/operators';

@Component({
  selector: 'app-activity-reservation-form',
  templateUrl: './activity-reservation-form.component.html',
  styleUrls: ['./activity-reservation-form.component.scss'],
  providers: [MessageService],
  standalone: false,
})
export class ActivityReservationFormComponent implements OnInit {

  @Input()  reservation: any;
  @Output() doAdd    = new EventEmitter<any>();
  @Output() doUpdate = new EventEmitter<any>();
  @Output() doDelete = new EventEmitter<any>();

  activeTab = 'details';
  formTitle: string;
  isNew: boolean;
  formLoading = false;
  form1: UntypedFormGroup;

  activities: any[]  = [];
  selectedActIds: number[] = [];

  reservationId: any;
  isReservationId = false;
  title = 'Reservation';

  readonly TABS = ['details', 'activities', 'notes'];

  get TIME_SLOTS()      { return this.activityReservationService.timeSlots; }
  get PAY_STATUSES()    { return this.activityReservationService.paymentStatuses; }
  get BOOKING_STATUSES(){ return this.activityReservationService.bookingStatuses; }

  get balance(): number {
    const due  = parseFloat(this.form1?.get('amountDue')?.value)  || 0;
    const paid = parseFloat(this.form1?.get('amountPaid')?.value) || 0;
    return due - paid;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private activityReservationService: ActivityReservationService,
    private activityService: ActivityService,
    private alertService: AlertService,
    private reservationService: ReservationService,
    @Inject(MAT_DIALOG_DATA) data: any,
  ) {
    this.formTitle  = data.title;
    this.reservation = data.reservation;
    this.activities  = data.activities || [];
  }

  ngOnInit(): void {
    this.isNew = !this.reservation?.activityReservationId;
    this.buildForm();

    if (this.reservation) {
      this.form1.patchValue(this.reservation);
      if (this.reservation.activityIds) {
        this.selectedActIds = this.reservation.activityIds
          .split(',').filter(Boolean).map((id: string) => +id.trim());
      }
    }
  }

  buildForm() {
    this.form1 = this.fb.group({
      activityReservationId: [this.reservation?.activityReservationId],
      reservationNumber:     [this.reservation?.reservationNumber || ''],
      // Guest
      firstName:      ['', Validators.required],
      lastName:       ['', Validators.required],
      email:          ['', [Validators.required, Validators.email]],
      phone:          [''],
      numGuests:      [1, [Validators.required, Validators.min(1)]],
      specialRequests:[''],
      // Stay
      property:       ['', Validators.required],
      unit:           [''],
      checkIn:        [''],
      checkOut:       [''],
      // Activity
      activityDate:   [''],
      preferredTime:  [''],
      // Payment
      amountDue:      [0],
      amountPaid:     [0],
      paymentStatus:  ['unpaid'],
      // Status
      bookingStatus:  ['pending'],
      // Notes
      staffNotes:     [''],
    });
  }

  isActivitySelected(id: number): boolean {
    return this.selectedActIds.includes(id);
  }

  toggleActivity(id: number) {
    const idx = this.selectedActIds.indexOf(id);
    if (idx === -1) {
      this.selectedActIds = [...this.selectedActIds, id];
    } else {
      this.selectedActIds = this.selectedActIds.filter(x => x !== id);
    }
  }

  activityById(id: number): any {
    return this.activities.find(a => a.activityId === id);
  }

  get selectedActivityObjects(): any[] {
    return this.selectedActIds
      .map(id => this.activityById(id))
      .filter(Boolean);
  }

  reservationInfoItem(event: any) {
    this.isReservationId = false;
    this.reservationId = event.reservationId;
    this.fetchReservationById(this.reservationId);
  }

  fetchReservationById(reservationId: any) {
    try {
      this.reservationService.findById(reservationId).subscribe(
        (res: any) => {
          const r = res.data.reservation;
          this.form1.patchValue({
            firstName:     r.guestProfile?.firstName,
            lastName:      r.guestProfile?.lastName,
            email:         r.guestProfile?.emailAddress,
            phone:         r.primaryMobilePhone,
            checkIn:       r.checkInDate  ? r.checkInDate.substring(0, 10)  : '',
            checkOut:      r.checkOutDate ? r.checkOutDate.substring(0, 10) : '',
            property:      r.shortTermRental?.developmentName,
            unit:          r.shortTermRental?.propertyCode,
            bookingStatus: r.reservationStatus,
          });
        },
        (err: any) => this.alertService.error(err)
      );
    } catch (error) {
      this.alertService.error(error);
    }
  }

  get activeActivities() {
    return this.activities.filter(a => a.status === 'active' || a.status === 'draft');
  }

  save() {
    this.form1.markAllAsTouched();
    if (this.form1.invalid) return;

    const payload = {
      ...this.form1.value,
      activityIds: this.selectedActIds.length > 0 ? this.selectedActIds.map(String).join(',') : '',
    };

    this.formLoading = true;
    const obs = this.isNew
      ? this.activityReservationService.add(payload)
      : this.activityReservationService.update(payload);

    obs.pipe(first()).subscribe(
      (res: any) => {
        const saved = res.data.activityReservation;
        if (this.isNew) this.doAdd.emit(saved);
        else             this.doUpdate.emit(saved);
        this.formLoading = false;
      },
      (err: any) => {
        this.alertService.error(err);
        this.formLoading = false;
      }
    );
  }

  cancelReservation() {
    const payload = {
      ...this.form1.value,
      bookingStatus: 'cancelled',
      paymentStatus: this.form1.get('paymentStatus')?.value === 'paid' ? 'refunded' : this.form1.get('paymentStatus')?.value,
    };
    this.activityReservationService.update(payload).pipe(first()).subscribe(
      (res: any) => this.doUpdate.emit(res.data.activityReservation),
      (err: any) => this.alertService.error(err)
    );
  }

  fmt$(n: number): string {
    return '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  errMsg(field: string): string | null {
    const c = this.form1.get(field);
    if (!c || !c.touched || c.valid) return null;
    if (c.hasError('required')) return 'Required';
    if (c.hasError('email'))    return 'Invalid email';
    if (c.hasError('min'))      return 'Must be at least 1';
    return 'Invalid';
  }
}
